/**
 * Cookie Consent Controller
 * Main controller that coordinates the cookie consent system
 */
import type { CookieConsent } from '../types';
import { CookieManager } from './cookieManager';
import { CookieConsentBanner } from '../ui/cookieConsentBanner';
import { CookieSettingsModal } from '../ui/cookieSettingsModal';

export class CookieConsentController {
    cookieManager: CookieManager;
    consentBanner: CookieConsentBanner;
    settingsModal: CookieSettingsModal;

    constructor() {
        this.cookieManager = new CookieManager();
        this.consentBanner = new CookieConsentBanner(this.cookieManager);
        this.settingsModal = new CookieSettingsModal(this.cookieManager);
        
        this.bindEvents();
        this.initialize();
    }

    /**
     * Initialize the cookie consent system
     */
    initialize(): void {
        // Check if user needs to see consent banner
        this.consentBanner.checkAndShow();
        
        // Initialize Google Analytics consent mode if available
        this.initializeGoogleAnalyticsConsent();
        
        // Log initialization
        console.log('Cookie Consent System initialized');
        
        const isDev = this.cookieManager.isDevelopment;
        console.log(`${isDev ? 'DEVELOPMENT' : 'PRODUCTION'} MODE: Modal will show once until consent is given`);
    }

    /**
     * Bind global event listeners
     */
    bindEvents(): void {
        // Listen for cookie consent changes
        window.addEventListener('cookieConsentChanged', (event: Event) => {
            const customEvent = event as CustomEvent<CookieConsent>;
            this.handleConsentChanged(customEvent.detail);
        });

        // Listen for hide cookie banner event
        window.addEventListener('hideCookieBanner', () => {
            this.consentBanner.hide();
        });

        // Handle page visibility change to potentially show banner again
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && !this.cookieManager.hasConsented()) {
                this.consentBanner.checkAndShow();
            }
        });
    }

    /**
     * Handle consent changes
     */
    handleConsentChanged(consent: CookieConsent): void {
        console.log('Cookie consent changed:', consent);
        
        // TODO: Implement additional logic when consent changes
        // For example, you could:
        // - Update third-party service configurations
        // - Send analytics events
        // - Update user preferences in backend
        
        // Example: Update analytics tracking
        if (window.analytics && typeof window.analytics.updateConsent === 'function') {
            window.analytics.updateConsent(consent);
        }
    }

    /**
     * Initialize Google Analytics consent mode
     */
    initializeGoogleAnalyticsConsent(): void {
        if (typeof window.gtag !== 'undefined') {
            const consent = this.cookieManager.getCookieConsent();
            
            // Set initial consent state
            window.gtag('consent', 'default', {
                'analytics_storage': consent.analytics ? 'granted' : 'denied',
                'ad_storage': consent.marketing ? 'granted' : 'denied',
                'wait_for_update': 500,
            });
        }
    }

    /**
     * Programmatically show cookie settings
     */
    showCookieSettings(): void {
        this.settingsModal.show();
    }

    /**
     * Programmatically accept all cookies
     */
    acceptAllCookies(): void {
        this.cookieManager.acceptAll();
        this.consentBanner.hide();
    }

    /**
     * Programmatically reject all cookies
     */
    rejectAllCookies(): void {
        this.cookieManager.rejectAll();
        this.consentBanner.hide();
    }

    /**
     * Get current consent status
     */
    getConsentStatus(): CookieConsent {
        return this.cookieManager.getCookieConsent();
    }

    /**
     * Check if specific cookie type is enabled
     */
    isCookieEnabled(type: keyof Omit<CookieConsent, 'hasConsented' | 'timestamp'>): boolean {
        return this.cookieManager.isCookieTypeEnabled(type);
    }

    /**
     * Reset consent (for testing purposes)
     */
    resetConsent(): void {
        if (this.cookieManager.isDevelopment) {
            console.log('DEVELOPMENT MODE: Reset not needed - modal shows every time');
        } else {
            localStorage.removeItem('cookieConsent');
            console.log('PRODUCTION MODE: Cookie consent reset');
        }
        this.cookieManager = new CookieManager();
        this.consentBanner.checkAndShow();
    }

    /**
     * Destroy the entire cookie consent system
     */
    destroy(): void {
        this.consentBanner.destroy();
        this.settingsModal.destroy();
        
        // Remove event listeners
        // Note: We can't easily remove the specific handlers, but they'll be garbage collected
    }
}
